import { extend } from 'quasar'
import { computed, readonly, ref, unref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { sortSanctionByExpiry } from '@/activities/helpers.js'
import { useSaveUserMutation } from '@/authuser/mutations'
import { useAuthService } from '@/authuser/services'
import i18n from '@/base/i18n'
import { onQueryError } from '@/base/queryClient.js'
import { useI18nService } from '@/base/services/i18n'
import groups from '@/group/api/groups'
import { sanctionIsActive, sortByRoleOrder } from '@/group/helpers.js'
import { parseGroupDetailQueryKey, useGroupDetailQuery, useSanctionsQuery } from '@/group/queries'
import { useGroupInfoService } from '@/groupInfo/services'
import { messages as loadMessages } from '@/locales'
import { usePlaceService } from '@/places/services'
import { useUserService } from '@/users/services'
import { useIntegerRouteParam } from '@/utils/composables'
import { defineService } from '@/utils/datastore/helpers'
import { captureException } from '@/utils/errors'

export const useCurrentGroupService = defineService(() => {
  // services
  const router = useRouter()
  const {
    isLoading: isLoadingUsers,
    getUserById,
  } = useUserService()

  const {
    getPlacesByGroup,
    isLoading: isLoadingPlaces,
  } = usePlaceService()

  const {
    groupId,
    selectGroup,
    clearGroup,
  } = useCurrentGroupId()

  const route = useRoute()

  const { groups } = useGroupInfoService()

  onQueryError((error, query) => {
    const queryGroupId = parseGroupDetailQueryKey(query.queryKey)
    if (queryGroupId && error?.response?.status === 404) { // TODO: could do for other errors too?
      if (route.meta.noRedirect) return

      // Not found! (only groups we are members of can be found) .. but it might exist for preview
      const groupPreview = groups.value.find(group => group.id === queryGroupId)
      if (groupPreview) {
        router.push({ name: 'groupPreview', params: { groupPreviewId: groupPreview.id } })
      }
      else {
        router.push({ name: 'groupsGallery' })
      }
      return true
    }
  })

  const {
    group,
    isLoading: isLoadingGroup,
    wait: waitForGroupToLoad,
  } = useGroupDetailQuery({ groupId })

  // computed
  const users = computed(() => (group.value?.memberships || []).map(membership => getUserById(membership.user)))
  const places = computed(() => getPlacesByGroup(groupId)
    .sort(sortByName))
  const features = computed(() => group.value?.features || [])
  const theme = computed(() => group.value?.theme)
  const isPlayground = computed(() => group.value?.status === 'playground')
  const isGeneralPurpose = computed(() => theme.value === 'general')
  const isBikeKitchen = computed(() => theme.value === 'bikekitchen')

  const roles = computed(() => group.value?.roles.slice().sort(sortByRoleOrder) ?? [])

  const memberRole = computed(() => roles.value.find(role => role.name === 'member'))

  // methods

  // Things that might be triggered on changes

  // keep users current group saved
  useSaveUserCurrentGroup({ groupId })

  // keep locale updated
  useSaveUserCurrentLocale()

  // keep set locale messages based on theme
  useLocaleMessagesSetter({ isBikeKitchen, isGeneralPurpose })

  // mark user active
  useMarkUserActive()

  return {
    selectGroup,
    clearGroup,
    group,
    groupId: readonly(groupId),
    waitForGroupToLoad,
    isLoadingGroup,
    isLoadingUsers,
    isLoadingPlaces,
    users,
    places,
    theme,
    isPlayground,
    isGeneralPurpose,
    isBikeKitchen,
    features,
    roles,
    memberRole,
  }
})

function useCurrentGroupId () {
  const {
    user,
    isLoggedIn,
  } = useAuthService()

  const groupId = ref(null)

  // Do our best to ensure we have a group id set, if we don't have one, get it from the route, or current user
  const groupIdRouteParam = useIntegerRouteParam('groupId')

  watchEffect(() => {
    if (isLoggedIn.value) {
      // Route param takes priority
      if (groupIdRouteParam.value) {
        groupId.value = groupIdRouteParam.value
      }
      // Otherwise if we don't have a group set already, use the user default
      if (!groupId.value) {
        groupId.value = user.value?.currentGroup
      }
    }
    else {
      groupId.value = null
    }
  })

  function selectGroup (value) {
    groupId.value = value
  }

  function clearGroup () {
    groupId.value = null
  }

  return {
    groupId: readonly(groupId),
    selectGroup,
    clearGroup,
  }
}

// TODO: this probably belongs in the i18nService ...
function useLocaleMessagesSetter ({ isBikeKitchen, isGeneralPurpose }) {
  const { locale } = useI18nService()
  const useGeneralPurposeMessages = computed(() => isBikeKitchen.value || isGeneralPurpose.value)

  watch(
    () => [useGeneralPurposeMessages.value, locale.value],
    async () => {
      if (!locale.value) return
      if (useGeneralPurposeMessages.value) {
        const generalPurposeMessages = await import('@/locales/generalPurpose.json')
        const messages = await loadMessages(locale.value)
        if (!useGeneralPurposeMessages.value) return
        const mergedMessages = extend(true, {}, messages, generalPurposeMessages)
        i18n.setLocaleMessage(locale.value, mergedMessages.default || mergedMessages)
      }
      else {
        const messages = await loadMessages(locale.value)
        if (useGeneralPurposeMessages.value) return

        i18n.setLocaleMessage(locale.value, messages.default || messages)
      }
    },
    {
      immediate: true,
    },
  )
}

function useMarkUserActive () {
  const router = useRouter()
  const route = useRoute()
  router.afterEach(async () => {
    try {
      if (route.params.groupId) await groups.throttledMarkUserActive(route.params.groupId)
    }
    catch (err) {
      captureException(err)
    }
  })
}

function useSaveUserCurrentGroup ({ groupId }) {
  const { user } = useAuthService()
  const { mutate: saveUser } = useSaveUserMutation()
  watch(groupId, value => {
    if (value) {
      if (user.value && user.value.currentGroup !== value) {
        saveUser({ currentGroup: value })
      }
    }
  }, { immediate: true })
}

function useSaveUserCurrentLocale () {
  const { user } = useAuthService()
  const { mutate: saveUser } = useSaveUserMutation()
  const { locale } = useI18nService()
  watch(locale, value => {
    if (value && user.value && user.value.language !== value) {
      saveUser({ language: value })
    }
  }, { immediate: true })
}

// TODO: move somewhere better
export function sortByName (a, b) {
  return a.name.localeCompare(b.name)
}

export function useHasFeature (featureName) {
  const { features } = useCurrentGroupService()
  return computed(() => features.value?.includes(featureName))
}

export function useCurrentGroupRoles () {
  const { roles } = useCurrentGroupService()
  return roles
}

export function useGetCurrentGroupRoleById () {
  const roles = useCurrentGroupRoles()
  return roleId => roles.value.find(role => role.id === roleId)
}

export function useRole (roleId) {
  const roles = useCurrentGroupRoles()
  return computed(() => roles.value.find(role => role.id === unref(roleId)))
}

export const useActiveRoleService = defineService(() => {
  const roleId = useIntegerRouteParam('roleId')
  const role = useRole(roleId)
  return {
    role,
    roleId,
  }
})
export const useSanctionService = defineService(() => {
  const {
    sanctions,
    wait: waitForSanctionsToLoad,
    isFetched,
  } = useSanctionsQuery()

  // Even though the API only returns non-expired sanctions,
  // we need to reactively check the expiresAt because:
  // a) it might have expired since we loaded it
  // b) websocket updates can modify the expiresAt (potentially...)
  const activeSanctions = computed(() => sanctions.value.filter(sanctionIsActive))

  // Utility for created filters + mapped-by-key sanctions
  function groupedSanctions (sanctionType, groupByKey) {
    return computed(() => {
      const result = {}
      for (const sanction of activeSanctions.value.filter(sanction => sanction.type === sanctionType)) {
        const key = sanction[groupByKey]
        if (Array.isArray(key)) {
          for (const keyItem of key) {
            if (!result[keyItem]) result[keyItem] = []
            result[keyItem].push(sanction)
          }
        }
        else {
          if (!result[key]) result[key] = []
          result[key].push(sanction)
        }
      }
      // Sort them all so the longest one comes on top as that will be still active
      // after the others have expired
      for (const key in result) {
        result[key] = result[key].sort(sortSanctionByExpiry)
      }
      return result
    })
  }

  return {
    waitForSanctionsToLoad,
    isFetched,

    // Group sanctions by groupId
    groupSanctions: groupedSanctions('group', 'group'),

    // Activity sanctions by placeId
    activitySanctions: groupedSanctions('activity', 'places'),

    // Role sanctions by roleId
    roleSanctions: groupedSanctions('role', 'roles'),
  }
})
